import { Button } from '@/components/common/Button';
import { Checkbox } from '@/components/common/Checkbox';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/common/DropdownMenu';
import { Form, FormControl, FormField, FormItem, FormLabel } from '@/components/common/Form';
import { Input } from '@/components/common/Input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/common/Tabs';
import { ActionDropdownItem } from '@/components/general/ActionDropdownItem';
import Table from '@/components/table/Table';
import { DEFAULT_SELECT_PAGE_SIZES, DISPLAY_MOMENT_DATE_TIME_HM, DISPLAY_MOMENT_DATE_YMD, FORUM, ROUTES, TOPIC, TOPIC_STATUS } from '@/configs/constants';
import { useStore } from '@/hooks/useStore';
import yup from '@/services/yup';
import { TOPIC_CREATOR_TYPE, TOPIC_STATUS_TYPE, TOPIC_TYPE_APPROVE, TopicTypeApprove } from '@/types/enums';
import { TopicTableItem } from '@/types/topic';
import { formatDateTime } from '@/utils/datetime';
import { toastify } from '@/utils/toastify';
import { scrollToTop } from '@/utils/utils';
import { yupResolver } from '@hookform/resolvers/yup';
import { ColumnDef, TableState, Updater } from '@tanstack/react-table';
import classNames from 'classnames';
import { BanIcon, CircleCheckIcon, CircleX, ClipboardCheck, EllipsisIcon, PlusIcon, RotateCcw, RotateCw, SearchIcon, SquarePenIcon, Trash2Icon } from 'lucide-react';
import { flowResult, toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Calendar from '@/components/calendar/Calendar';
import { Label } from '@/components/common/Label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/common/Select';
import ConfirmPopup from '../components/ConfirmPopup';
import { Textarea } from '@/components/common/Textarea';

export default observer(function TopicList() {
    // hooks
    const { t } = useTranslation();

    const navigate = useNavigate();

    // store
    const { topicStore: { keepActivedTab, keepSearchParams, totalByStatus, paging, topicList, totalRecord, defaultPaging, getAllTopic, setObservable },
        modalStore: { showModal }
    } = useStore();

    // state
    const [activeTab, setActiveTab] = useState(keepActivedTab ? keepActivedTab : '');
    const [searchState, setSearchState] = useState<Object>(keepSearchParams ?? '');

    // validate schema
    const validateSchema = yup.object().shape({
        title: yup.string(),
        fromCreatedDate: yup.date(),
        toCreatedDate: yup.date(),
        creatorRole: yup.string(),
        creatorName: yup.string(),
        forums: yup.array().of(yup.number()),
        types: yup.array().of(yup.number()),
        status: yup.number()
    });

    type FormData = yup.InferType<typeof validateSchema>;

    const form = useForm<FormData>({
        resolver: yupResolver(validateSchema),
        mode: 'onSubmit',
        defaultValues: {
            title: '',
            fromCreatedDate: undefined,
            toCreatedDate: undefined,
            creatorRole: 'all',
            creatorName: '',
            forums: [],
            types: [],
            status: undefined
        }
    });

    // lifecycle
    useEffect(() => {
        if (keepSearchParams) {
            form.reset(keepSearchParams);
            onFetch(keepSearchParams, paging);
        }
    }, []);

    useEffect(() => {
        onSearch(searchState);
    }, [activeTab]);

    // functions
    const onFetch = (searchParams?: any, paging?: TableState) => {
        if (paging) {
            setObservable('paging', paging ?? null);
        }
        const paramsToSend = {
            ...searchParams,
            creatorRole: searchParams?.creatorRole === 'all' ? '' : searchParams?.creatorRole,
            status: searchParams?.status === 'all' ? '' : searchParams?.status,
            createdDateStart: formatDateTime(searchParams?.fromCreatedDate, DISPLAY_MOMENT_DATE_YMD) || '',
            createdDateEnd: formatDateTime(searchParams?.toCreatedDate, DISPLAY_MOMENT_DATE_YMD) || ''

        };

        setObservable('keepActivedTab', activeTab);
        setObservable('keepSearchParams', toJS(searchParams));
        getAllTopic({ ...paramsToSend, status: activeTab }, paging);
    };

    const onSearch = (searchParams: FormData) => {
        setSearchState(searchParams);
        onFetch(searchParams, defaultPaging);
    };

    const handleTabChange = (tabValue: string) => {
        setActiveTab(tabValue);
    };

    const onStateChange = (stateUpdater: Updater<TableState>) => {
        const newPaging = stateUpdater instanceof Function ? stateUpdater(paging) : stateUpdater;
        onFetch(searchState, newPaging);
        scrollToTop();
    };

    const handleClickNewTopic = () => {
        // showModal({
        //     size: '4xl',
        //     title: t('words_title.upload_new_topic'),
        //     content: (
        //         <TopicForm onFetch={() => {
        //             onFetch(keepSearchParams, paging);
        //         }}
        //         />
        //     ),
        //     footerClassName: 'hidden'
        // });
        navigate(ROUTES.topicCreate.href);
    };

    // columns
    const columns: ColumnDef<TopicTableItem>[] = [
        {
            header: t('words_title.index'),
            size: 10,
            enableSorting: false,
            cell: ({ row: { index } }) => (
                <div>{paging.pagination.pageIndex * paging.pagination.pageSize + index + 1}</div>
            )
        },
        {
            header: t('words_title.title'),
            size: 250,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <div>
                    <p className='line-clamp-2'>{original.title}</p>
                </div>
            )
        },
        {
            header: t('words_title.forum'),
            size: 120,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <div>{t(`words_title.${FORUM[original.forum].key}`)}</div>
            )
        },
        {
            header: t('words_title.type_of_topic'),
            size: 120,
            enableSorting: false,
            cell: ({ row: { original } }) =>
                original.type ?
                    (
                        <div>{t(`words_title.${TOPIC[original.type].key}`)}</div>
                    ) :
                    null
        },

        {
            header: t('words_title.created_by'),
            size: 100,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <div>
                    {
                        original?.creator?.isDeleted ?
                            (
                                <div>{original?.creator?.fullName ?? ''}</div>
                            ) :
                            (
                                <a className='underline text-link-1 cursor-pointer' target='_blank' href={`/users/${original?.creator?.code}`} rel='noreferrer'>{original?.creator?.fullName ?? ''}</a>
                            )
                    }
                </div>
            )
        },
        {
            header: t('words_title.created_date'),
            size: 160,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <div>{formatDateTime(original.createdAt, DISPLAY_MOMENT_DATE_TIME_HM)}</div>
            )
        },
        {
            header: t('words_title.last_updated'),
            size: 160,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <div>{formatDateTime(original.updatedAt, DISPLAY_MOMENT_DATE_TIME_HM)}</div>
            )
        },
        {
            header: t('words_title.status'),
            size: 130,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <div className='flex items-center justify-start gap-2'>
                    <div className={classNames('w-2 h-2 rounded-full flex-none', TOPIC_STATUS[original.status].color)}></div>
                    <div>{t(`words_title.${TOPIC_STATUS[original.status].key}`)}</div>
                </div>
            )
        },
        {
            id: 'actions',
            size: 10,
            cell: ({ row: { original } }) => (
                <div className='flex justify-center items-center'>
                    <ActionMenuDropdown
                        status={original.status}
                        code={original.code}
                        onFetch={onFetch}
                    />
                </div>
            )
        }
    ];

    return (
        <div className='pb-4'>
            <Form {...form}>
                <form onSubmit={form.handleSubmit(onSearch)}>
                    <div className='bg-white rounded p-4'>
                        <div className='grid grid-cols-2 gap-x-20 gap-y-4'>
                            {/* title */}
                            <div>
                                <FormField
                                    control={form.control}
                                    name='title'
                                    render={({ field }) => (
                                        <FormItem className='flex gap-2 items-center'>
                                            <FormLabel className='w-1/5'>{t('words_title.title')}</FormLabel>
                                            <FormControl className='w-4/5'>
                                                <Input type='text' {...field} />
                                            </FormControl>
                                        </FormItem>
                                    )}
                                />
                            </div>
                            {/* create date */}
                            <div className='flex items-start'>
                                <Label className='w-1/5 label-1 h-[48px] leading-[48px]'>{t('words_title.promotion_created_date')}</Label>
                                <div className='flex items-center gap-3 w-4/5 ml-2'>
                                    <FormField
                                        control={form.control}
                                        name='fromCreatedDate'
                                        render={({ field }) => (
                                            <FormItem className='w-1/2'>
                                                <FormControl>
                                                    <Calendar
                                                        mode='single'
                                                        selected={field.value}
                                                        onSelect={field.onChange}

                                                        useIconRemove={true}
                                                        className='w-full'
                                                        placeholder={t('placeholder.from')}
                                                        required
                                                    />
                                                </FormControl>

                                            </FormItem>
                                        )}
                                    />
                                    <div> - </div>
                                    <FormField
                                        control={form.control}
                                        name='toCreatedDate'
                                        render={({ field }) => (
                                            <FormItem className='w-1/2'>
                                                <FormControl>
                                                    <Calendar
                                                        mode='single'
                                                        selected={field.value}
                                                        onSelect={field.onChange}

                                                        useIconRemove={true}
                                                        className='w-full'
                                                        placeholder={t('placeholder.to')}
                                                        required
                                                    />
                                                </FormControl>
                                            </FormItem>
                                        )}
                                    />
                                </div>
                            </div>
                            {/* forums */}
                            <div>
                                <FormField
                                    control={form.control}
                                    name='forums'
                                    render={({ field }) => (
                                        <FormItem className='flex gap-2 items-center h-full'>
                                            <FormLabel className='w-1/5'>{t('words_title.forum')}</FormLabel>
                                            <FormControl className='w-4/5'>
                                                <div className='flex gap-8'>
                                                    {Object.values(FORUM).map(({ key, value }) => (
                                                        <div key={key} className='flex items-center gap-2'>
                                                            <Checkbox
                                                                id={`${key}Checkbox`}
                                                                onCheckedChange={(checked) => {
                                                                    return checked && field.value ?
                                                                        field.onChange([...field.value, value]) :
                                                                        field.onChange(
                                                                            field.value?.filter(
                                                                                v => v !== value
                                                                            )
                                                                        );
                                                                }}
                                                            />
                                                            <label className='cursor-pointer' htmlFor={`${key}Checkbox`}>{t(`words_title.${key}`)}</label>
                                                        </div>
                                                    ))}
                                                </div>
                                            </FormControl>
                                        </FormItem>
                                    )}
                                />
                            </div>
                            {/* creator */}
                            <div className='flex flex-row'>
                                <Label className='w-1/5 label-1 h-[48px] leading-[48px]'>{t('words_title.promotion_created_by')}</Label>
                                <div className='flex w-4/5 ml-2 gap-3'>
                                    <FormField
                                        control={form.control}
                                        name='creatorRole'
                                        render={({ field }) => (
                                            <FormItem className='w-[30%]'>
                                                <div className='flex items-center w-full '>

                                                    <Select

                                                        key={field.value}
                                                        onValueChange={field.onChange}
                                                        {...field}
                                                        className='w-full'
                                                    >

                                                        <FormControl>
                                                            <SelectTrigger>
                                                                <SelectValue placeholder={t('placeholder.choose')} />
                                                            </SelectTrigger>
                                                        </FormControl>
                                                        <SelectContent>
                                                            <SelectItem
                                                                className='hover:cursor-pointer'
                                                                value='all'
                                                            >
                                                                <span>{t('words_title.all')}</span>
                                                            </SelectItem>
                                                            <SelectItem
                                                                className='hover:cursor-pointer'
                                                                value={TOPIC_CREATOR_TYPE.Admin.toString()}
                                                            >
                                                                <span>{t('words_title.admin')}</span>
                                                            </SelectItem>
                                                            <SelectItem
                                                                className='hover:cursor-pointer'
                                                                value={TOPIC_CREATOR_TYPE.Enduser.toString()}
                                                            >
                                                                <span>{t('words_title.enduser')}</span>
                                                            </SelectItem>

                                                        </SelectContent>
                                                    </Select>
                                                </div>

                                            </FormItem>
                                        )}
                                    />
                                    <FormField
                                        control={form.control}
                                        name='creatorName'
                                        render={({ field }) => (
                                            <FormItem className='flex gap-2 items-center w-[70%]'>

                                                <FormControl className=''>
                                                    <Input type='text' {...field} placeholder={t('placeholder.search_by_user_name')} />
                                                </FormControl>
                                            </FormItem>
                                        )}
                                    />
                                </div>
                            </div>
                            {/* types */}
                            <div>
                                <FormField
                                    control={form.control}
                                    name='types'
                                    render={({ field }) => (
                                        <FormItem className='flex gap-2 items-center h-full'>
                                            <FormLabel className='w-1/5'>{t('words_title.type_of_topic')}</FormLabel>
                                            <FormControl className='w-4/5'>
                                                <div className='flex gap-8'>
                                                    {Object.values(TOPIC).map(({ key, value }) => (
                                                        <div key={key} className='flex items-center gap-2'>
                                                            <Checkbox
                                                                id={`${key}Checkbox`}
                                                                onCheckedChange={(checked) => {
                                                                    return checked && field.value ?
                                                                        field.onChange([...field.value, value]) :
                                                                        field.onChange(
                                                                            field.value?.filter(
                                                                                v => v !== value
                                                                            )
                                                                        );
                                                                }}
                                                            />
                                                            <label className='cursor-pointer' htmlFor={`${key}Checkbox`}>{t(`words_title.${key}`)}</label>
                                                        </div>
                                                    ))}
                                                </div>
                                            </FormControl>
                                        </FormItem>
                                    )}
                                />
                            </div>
                        </div>
                        <div className='flex justify-center mt-8'>
                            <Button variant='outline' className='min-w-48 flex items-center justify-center gap-2 group'>
                                <SearchIcon className='w-5 h-5 text-active group-hover:text-white' />
                                <span>{t('button.search')}</span>
                            </Button>
                        </div>
                    </div>
                </form>
            </Form>

            <Tabs value={activeTab} onValueChange={handleTabChange} className='w-full h-full flex flex-col'>
                <div className='bg-white mt-4 rounded p-4'>
                    <Button
                        onClick={handleClickNewTopic}
                        variant='default'
                        className='text-white flex items-center justify-center gap-2 min-w-28 ml-auto'
                    >
                        <PlusIcon className='w-7 h-7 text-white' />
                        <span>{t('button.new_topic')}</span>
                    </Button>
                    <TabsList variant='shop' data-orientation='horizontal' className='flex w-full mt-6'>
                        <TabsTrigger
                            variant='shop'
                            className='md:text-[0.875rem] text-3 w-full'
                            value=''
                        >
                            {`${t('words_title.all_topics')} (${Object.values(totalByStatus).reduce((accumulator, value) => accumulator += value, 0)})`}
                        </TabsTrigger>
                        {
                            Object.entries([
                                TOPIC_STATUS[TOPIC_STATUS_TYPE.WaitingToApprove],
                                TOPIC_STATUS[TOPIC_STATUS_TYPE.Published],
                                TOPIC_STATUS[TOPIC_STATUS_TYPE.NotPublished],
                                TOPIC_STATUS[TOPIC_STATUS_TYPE.Rejected]
                            ]).map(([key, value], index) => (
                                <TabsTrigger
                                    key={index}
                                    variant='shop'
                                    className='md:text-[0.875rem] text-3 w-full'
                                    value={value.value.toString()}
                                >
                                    {`${t(`words_title.${value.key}`)} (${totalByStatus?.[value.value] ?? '0'})`}
                                </TabsTrigger>
                            ))
                        }
                    </TabsList>
                </div>

                <div className='bg-white rounded p-4'>
                    <TabsContent value=''>
                        <Table
                            columns={columns}
                            data={topicList}
                            rowCount={totalRecord}
                            onStateChange={onStateChange}
                            state={paging}
                            selectPageSizes={DEFAULT_SELECT_PAGE_SIZES}
                            getRowId={row => row.id?.toString()}
                        />
                    </TabsContent>
                    {
                        Object.entries([
                            TOPIC_STATUS[TOPIC_STATUS_TYPE.WaitingToApprove],
                            TOPIC_STATUS[TOPIC_STATUS_TYPE.Published],
                            TOPIC_STATUS[TOPIC_STATUS_TYPE.NotPublished],
                            TOPIC_STATUS[TOPIC_STATUS_TYPE.Rejected]
                        ]).map(([key, value], index) => (
                            <TabsContent value={value.value.toString()} key={index}>
                                <Table
                                    columns={columns}
                                    data={topicList}
                                    rowCount={totalRecord}
                                    onStateChange={onStateChange}
                                    state={paging}
                                    selectPageSizes={DEFAULT_SELECT_PAGE_SIZES}
                                    getRowId={row => row.id?.toString()}
                                />
                            </TabsContent>
                        ))
                    }
                </div>
            </Tabs>
        </div>
    );
});

type ActionMenuDropdownProps = {
    status: number,
    code: string,
    onFetch: Function
};

const ActionMenuDropdown = ({ status, code, onFetch }: ActionMenuDropdownProps) => {
    // hooks
    const { t } = useTranslation();
    const navigate = useNavigate();

    // store
    const {
        topicStore: { updateTopicStatus, deleteTopic, updateApproveTopic, keepSearchParams, paging, topicDetail },
        modalStore: { showAlertModal, modalWaringAlertOptions, hideModal }
    } = useStore();

    // function
    const handleClickUnpublish = () => {
        showAlertModal(modalWaringAlertOptions({
            hideIconCenter: true,
            content: t('messages.confirm_unpublished_topic'),
            onSave: async () => {
                const res = await flowResult(updateTopicStatus({ code, status: TOPIC_STATUS_TYPE.NotPublished }));
                if (res && res.ok) {
                    onFetch(keepSearchParams, paging);
                    toastify('success', t('messages.update_success'));
                }
            }
        }));
    };

    const handleClickPublish = () => {
        showAlertModal(modalWaringAlertOptions({
            hideIconCenter: true,
            content: t('messages.confirm_published_topic'),
            onSave: async () => {
                const res = await flowResult(updateTopicStatus({ code, status: TOPIC_STATUS_TYPE.Published }));
                if (res && res.ok) {
                    onFetch(keepSearchParams, paging);
                    toastify('success', t('messages.update_success'));
                }
            }
        }));
    };

    const handleClickApproveAndReject = (type: TopicTypeApprove) => {
        if (type === TOPIC_TYPE_APPROVE.REJECT) {
            showAlertModal(
                modalWaringAlertOptions({
                    hideIconCenter: true,
                    cancelButton: false,
                    saveButton: false,
                    content: (
                        <RejectModalContent
                            onClose={() => hideModal('warning-modal')}
                            onSave={async (reason: string) => {
                                if (code) {
                                    const payload = { typeApprove: type, reason };
                                    const res = await flowResult(updateApproveTopic(code, payload));
                                    if (res?.ok) toastify('success', t('messages.update_success'));
                                    onFetch(keepSearchParams, paging);
                                }
                                hideModal('warning-modal');
                            }}
                        />
                    )

                })
            );
        } else {
            // approve / re-public
            showAlertModal(
                modalWaringAlertOptions({
                    hideIconCenter: true,
                    cancelButton: <>{t('button.cancel')}</>,
                    saveButton: <>{t(`button.${type}`)}</>,
                    content: (
                        <span className='w-full'>
                            {type === TOPIC_TYPE_APPROVE.APPROVE ?
                                t('words_title.are_you_sure_you_want_to_approve_this_post') :
                                (
                                    <span className='flex items-center w-full justify-center'>
                                        {t('words_title.are_you_sure_you_want_to_republish_this_post')}
                                        <br />
                                        {t('words_title.if_you_agree_the_post_will_be_reapproved_before_it_is_officially_published')}
                                    </span>
                                )}
                        </span>
                    ),
                    onSave: async () => {
                        if (code) {
                            const payload = { typeApprove: type, reason: '' };
                            const res = await flowResult(updateApproveTopic(code, payload));
                            if (res?.ok) toastify('success', t('messages.update_success'));
                            onFetch(keepSearchParams, paging);
                        }
                    }
                })
            );
        }
    };

    const handleClickDelete = () => {
        showAlertModal(modalWaringAlertOptions({
            hideIconCenter: true,
            content: t('messages.confirm_delete_topic'),
            onSave: async () => {
                const res = await flowResult(deleteTopic(code));
                if (res && res.ok) {
                    onFetch(keepSearchParams, paging);
                    toastify('success', t('messages.delete_success'));
                }
            }
        }));
    };

    const handleClickEdit = () => {
        navigate(`${code}`);
    };

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <EllipsisIcon className='h-6 w-6 text-active hover:cursor-pointer' />
            </DropdownMenuTrigger>

            <DropdownMenuContent className='w-48' align='end' forceMount>

                {
                    status === TOPIC_STATUS_TYPE.Published && (
                        <>
                            <DropdownMenuItem className='hover:cursor-pointer' asChild>
                                <ActionDropdownItem icon={BanIcon} text={t('button.unpublish')} onClick={handleClickUnpublish} />
                            </DropdownMenuItem>
                        </>
                    )
                }

                {
                    status === TOPIC_STATUS_TYPE.NotPublished && (
                        <>
                            <DropdownMenuItem className='hover:cursor-pointer' asChild>
                                <ActionDropdownItem icon={CircleCheckIcon} text={t('button.publish')} onClick={handleClickPublish} />
                            </DropdownMenuItem>
                        </>
                    )
                }

                {
                    status === TOPIC_STATUS_TYPE.Rejected && (
                        <>
                            <DropdownMenuItem
                                className='hover:cursor-pointer'
                                onSelect={() => handleClickApproveAndReject(TOPIC_TYPE_APPROVE.RE_PUBLIC)}
                                asChild
                            >
                                <ActionDropdownItem icon={RotateCcw} text={t('button.re_public')} />
                            </DropdownMenuItem>
                        </>

                    )
                }
                {
                    status === TOPIC_STATUS_TYPE.WaitingToApprove && (
                        <>
                            <DropdownMenuItem
                                className='hover:cursor-pointer'
                                onSelect={() => handleClickApproveAndReject(TOPIC_TYPE_APPROVE.APPROVE)}
                                asChild
                            >
                                <ActionDropdownItem icon={ClipboardCheck} text={t('button.approve')} />
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                                className='hover:cursor-pointer'
                                onSelect={() => handleClickApproveAndReject(TOPIC_TYPE_APPROVE.REJECT)}
                                asChild
                            >
                                <ActionDropdownItem icon={CircleX} text={t('button.reject')} />
                            </DropdownMenuItem>
                        </>
                    )
                }
                <DropdownMenuSeparator />
                <DropdownMenuItem className='hover:cursor-pointer' asChild>
                    <ActionDropdownItem icon={SquarePenIcon} text={t('words_title.edit_action')} onClick={handleClickEdit} />
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem className='hover:cursor-pointer' asChild>
                    <ActionDropdownItem icon={Trash2Icon} text={t('words_title.delete_action')} onClick={handleClickDelete} />
                </DropdownMenuItem>

            </DropdownMenuContent>
        </DropdownMenu>
    );
};

const RejectModalContent: React.FC<{
    onClose: () => void,
    onSave: (reason: string) => void

}> = ({ onClose, onSave }) => {
    const [reason, setReason] = React.useState('');
    const { t } = useTranslation();

    return (
        <div className='flex flex-col items-start w-full'>
            <span>{t('words_title.are_you_sure_you_want_to_reject_this_post')}</span>
            <div className='flex items-start gap-4 mt-3 w-full'>
                <Label className='label-1 w-[14%] flex items-start'>{t('words_title.reason')}</Label>
                <Textarea
                    className='flex-1 resize-none h-[133px]'
                    value={reason}
                    onChange={e => setReason(e.target.value)}
                    maxLength={1000}

                />
            </div>

            <div className='flex justify-center gap-6 mt-5 w-full'>
                <Button className='w-1/2' variant='outline' onClick={onClose}>
                    {t('button.cancel')}
                </Button>
                <Button
                    onClick={() => onSave(reason)}
                    className='bg-[var(--active)] w-1/2 text-white hover:bg-[var(--active)]'
                >
                    {t('button.reject')}
                </Button>
            </div>
        </div>
    );
};
